import { LegacyRef, forwardRef, useState } from 'react';
import * as React from 'react';
import { SelectInputOption } from '../../types/sharedTypes';
import { ArrowIcon } from './ArrowIcon';
import closeIcon from '../../assets/close.svg';
import classNames from 'classnames';
import useOutsideClick from '../../hooks/useOutsideClick';

interface SelectInputProps {
  label?: string;
  description?: string;
  disabled?: boolean;
  options: SelectInputOption[];
  onChange: (event: {
    target: { value: string | number; name: string };
  }) => void;
  value: string | number;
  name: string;
  placeholder?: string;
}

const SearchSelectInput = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (props: SelectInputProps, _ref: LegacyRef<HTMLDivElement>) => {
    const {
      label,
      description,
      disabled = false,
      options,
      value,
      onChange,
      name,
      placeholder = 'Select an option',
    } = props;
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [searchText, setSearchText] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isMouseOutside, setIsMouseOutside] = useState(true);

    const optionsRef = useOutsideClick(() => setIsOpen(false));

    const handleMouseEnter = () => {
      setIsMouseOutside(false);
    };

    const handleMouseLeave = () => {
      setIsMouseOutside(true);
    };

    const getLabelByValue = () => {
      return (
        options.find((option) => option.value === value)?.label ?? placeholder
      );
    };

    const toggleSelect = () => {
      if (!disabled) setIsOpen(!isOpen);
      if (isOpen) setIsMouseOutside(true);
    };

    const handleOptionClick = (option: SelectInputOption) => {
      if (onChange) onChange({ target: { value: option.value, name } });
      setIsOpen(false);
      setIsMouseOutside(false);
      clearSearch();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!disabled) {
        if ((e.key === 'ArrowDown' || e.key === 'Enter') && !isOpen) {
          e.preventDefault();
          setIsOpen(true);
        } else if (e.key === 'Escape') {
          setIsOpen(false);
        }
      }
    };

    const filterOptions = (e: { target: { value: string } }) => {
      let filteredOptions = options;
      if (e.target.value.length > 0) {
        const regex = new RegExp(e.target.value, 'i');
        filteredOptions = options.filter((option) => regex.test(option.label));
      }
      setFilteredOptions(filteredOptions);
      setSearchText(e.target.value);
    };

    const clearSearch = () => {
      setFilteredOptions(options);
      setSearchText('');
    };

    return (
      <div
        data-test-id='select-input-containter'
        className='w-full mb-5 relative bg-white'
      >
        {label && (
          <label
            htmlFor={name}
            className='text-sm font-medium text-dark-header'
          >
            {label}
          </label>
        )}
        {description && (
          <p className='text-xs mt-1 mb-1 font-normal leading-4 text-gray-500'>
            {description}
          </p>
        )}
        <div
          id={name}
          onKeyDown={handleKeyDown}
          ref={optionsRef}
          role='combobox'
          data-test-id={label}
          aria-expanded={isOpen}
          aria-haspopup='listbox'
          tabIndex={disabled ? -1 : 0}
          className='w-full relative'
        >
          <div
            onClick={toggleSelect}
            ref={optionsRef}
            className={classNames(
              `w-full flex items-center justify-between p-[10px] border rounded`,
              { 'bg-gray-100 cursor-not-allowed': disabled },
              { 'border-gray-200 cursor-pointer': !disabled },
            )}
          >
            <p className='text-sm leading-5 text-gray-600'>
              {getLabelByValue()}
            </p>
            <ArrowIcon />
          </div>
          {isOpen && (
            <ul
              role='listbox'
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className={classNames(
                `list-none p-0 m-0 absolute top-full left-0 w-full border`,
                `border-gray-200 mt-1 z-10 rounded max-h-80 overflow-x-auto bg-white`,
                { 'bg-gray-200': disabled },
              )}
            >
              <li>
                <div
                  className={classNames(
                    `w-full flex items-center justify-between border rounded`,
                    { 'bg-gray-100 cursor-not-allowed': disabled },
                    { 'border-gray-200 cursor-pointer': !disabled },
                  )}
                >
                  <input
                    type='text'
                    value={searchText}
                    onChange={filterOptions}
                    placeholder='Search'
                    className={classNames(
                      'text-sm leading-5 pt-[10px] pb-[10px] border-none',
                      'rounded outline-none w-full',
                    )}
                  />
                  <div className='pr-[10px]'>
                    <img
                      src={closeIcon}
                      onClick={clearSearch}
                      width={10}
                      alt='clear search'
                      className='ml-3 mr-1'
                    />
                  </div>
                </div>
              </li>
              {filteredOptions.length > 0 &&
                filteredOptions.map((option) => (
                  <li
                    key={option.value}
                    onClick={() => handleOptionClick(option)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleOptionClick(option);
                        setIsOpen(false);
                      }
                    }}
                    className={classNames(
                      `p-[10px] cursor-pointer text-sm hover:bg-gray-200 hover:shadow-md`,
                      {
                        'bg-gray-200 border-r-2 border-purple-600 rounded-tl rounded-bl':
                          option.value === value && !!isMouseOutside,
                      },
                    )}
                    role='option'
                    tabIndex={0}
                  >
                    {option.label}
                  </li>
                ))}
              {filteredOptions.length === 0 && (
                <li className='p-[10px] text-sm'>No results</li>
              )}
            </ul>
          )}
        </div>
      </div>
    );
  },
);
export default SearchSelectInput;
